import { Box, Card, CardContent, Typography } from "@mui/material";
import { CHART_COLORS } from "../../../../components/charts/chartTheme";

import { PieChart } from "@mui/x-charts/PieChart";
import { AttendanceMonthlySummary } from "../types";
import { PlaceholderBadge } from "./PlaceholderBadge";
import { EmptyState } from "./EmptyState";
import { cardTitleSx, dashboardCardSx } from "../responsiveText";

type AttendanceDonutCardProps = {
  data: AttendanceMonthlySummary;
};

export const AttendanceDonutCard = ({ data }: AttendanceDonutCardProps) => {
  const total = data.present + data.absent + data.leave;
  const presentPercent = total === 0 ? 0 : Math.round((data.present / total) * 1000) / 10;

  if (total === 0) {
    return (
      <Card elevation={0} sx={dashboardCardSx}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600} sx={cardTitleSx}>
                Attendance
              </Typography>
              <Typography variant="caption" color="text.secondary">
                This month
              </Typography>
            </Box>
            <PlaceholderBadge />
          </Box>
          <EmptyState message="No attendance data yet" height={200} />
        </CardContent>
      </Card>
    );
  }

  const series = [
    { id: 0, value: data.present, label: "Present", color: CHART_COLORS.series1 },
    { id: 1, value: data.absent, label: "Absent", color: CHART_COLORS.series2 },
    { id: 2, value: data.leave, label: "Leave", color: CHART_COLORS.series3 },
  ];

  return (
    <Card elevation={0} sx={dashboardCardSx}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600} sx={cardTitleSx}>
              Attendance
            </Typography>
            <Typography variant="caption" color="text.secondary">
              This month
            </Typography>
          </Box>
          <PlaceholderBadge />
        </Box>

        <Box sx={{ position: "relative", mt: 1 }}>
          <PieChart
            series={[{ data: series, innerRadius: 52, outerRadius: 78, paddingAngle: 2, cornerRadius: 4 }]}
            height={200}
            hideLegend
          />
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center",
              pointerEvents: "none",
            }}
          >
            <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#0f172a", lineHeight: 1.1 }}>
              {presentPercent}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              present
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 1 }}>
          {series.map((s) => (
            <Box key={s.id} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "3px", backgroundColor: s.color, flexShrink: 0 }} />
              <Typography variant="body2">{s.label}</Typography>
              <Typography variant="body2" fontWeight={600} sx={{ ml: "auto" }}>
                {s.value} day{s.value === 1 ? "" : "s"}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};
